<template>
    <div class="head">
        <div class="title">{{ title }}</div>
        <GoHomeButton @click.prevent="onGoHomeButtonClick" />
        <UserMenuComponent />
        <LanguageSelector v-if="kiosk.isKioskMode" />
    </div>

    <PreorderTimeComponent
        v-model:selectedOrderDateAndTime="selectedOrderDateAndTime"
        :selectedServiceId="selectedServiceId"
        :showHeader="true"
        :initializeOnMounted="false"
        @orderDateAndTimeSelected="onOrderDateAndTimeSelected"
        ref="preorderTimeComponent"
    />

    <div class="preorder-time-view-bottom" ref="bottomElement">
        <!-- pay button -->
    </div>
</template>

<script setup lang="ts">
    const kiosk = useKioskStore();
    import { TastierClient } from "@/app/TastierClient";
    import { PreorderTimeController } from "@/app/controller/PreorderTimeController";
    import type { PreorderTimeControllerShownEventData } from "@/app/controller/PreorderTimeController";
    import { PayButton } from "@/app/elements/PayButton";
    import { appEvents } from "@/app/events/appEvents";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import type { SelectedOrderDateAndTime } from "@/data/model/Customer";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { computed, onMounted, ref, watch } from "vue";

    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import PreorderTimeComponent from "@/components/preorder/PreorderTimeComponent.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";

    // define state
    const selectedServiceId = ref<string | null>(null);

    // define template refs
    const preorderTimeComponent = ref<InstanceType<typeof PreorderTimeComponent> | null>(null);

    // Header's title
    const title = computed<string>(() => {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        return serviceDesignation == null ? trans.order.title : serviceDesignation;
    });

    // order date time form
    const selectedOrderDateAndTime = ref<SelectedOrderDateAndTime | null>(null);
    const isOrderDateAndTimeSelected = computed(
        () => selectedOrderDateAndTime.value?.date != null && selectedOrderDateAndTime.value?.time != null
    );

    // define functions
    async function initialize() {
        selectedServiceId.value = TastierClient.context.selectedServiceId ?? null;

        preorderTimeComponent.value?.initialize();
    }

    function onOrderDateAndTimeSelected() {
        PreorderTimeController.onContinue.dispatch();
    }

    // #region View visibility
    const areEventsInitialized = ref(false);
    const isViewVisible = ref(false);

    PreorderTimeController.onShown.add(onBecomeVisible, this);
    PreorderTimeController.onHidden.add(onBecomeInvisible, this);

    function onBecomeVisible(this: any, eventData?: PreorderTimeControllerShownEventData) {
        isViewVisible.value = true;

        if (eventData?.isMainNavigation) {
            // ts-1251: If we initialize this view too soon, it might throw 400/500 errors while loading fulfillment slots because of switching between the tables
            // we need to wait for app to be initialized before we initialize this view
            if (TastierClient.isInitialized) {
                initialize();
            } else {
                appEvents.tastierClient.initialized.addOnce(initialize);
            }
        }

        if (!areEventsInitialized.value) {
            areEventsInitialized.value = true;
        }
    }

    function onBecomeInvisible(this: any) {
        preorderTimeComponent.value?.reset();
        isViewVisible.value = false;

        if (areEventsInitialized.value) {
            areEventsInitialized.value = false;
            appEvents.tastierClient.initialized.remove(initialize);
        }
    }

    onMounted(() => {
        onBecomeVisible({ isMainNavigation: true });
    });
    // #endregion

    // #region View Header config
    function onGoHomeButtonClick() {
        PreorderTimeController.onShowServices.dispatch();
    }
    // #endregion

    // #region View Bottom config
    // html refs
    const bottomElement = ref<HTMLElement | null>(null);

    // components
    const payBtn = ref<PayButton | null>(null);

    onMounted(() => {
        const $bottom = $(bottomElement.value as any);
        payBtn.value = new PayButton(trans.general.continue).appendTo($bottom);
        payBtn.value.setAmount(toMoneyWithSign(0));
        payBtn.value.setDisabled();
        payBtn.value.onClick = () => {
            preorderTimeComponent.value?.selectOrderDateAndTime();
        };
    });

    watch(isOrderDateAndTimeSelected, (newVal) => {
        if (newVal) {
            payBtn.value?.setEnabled();
        } else {
            payBtn.value?.setDisabled();
        }
    });
    // #endregion
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .view.preorder-time {
        display: flex;
        flex-direction: column;

        > .head {
            flex: 0 0 auto;
        }

        > .scrollable-view {
            flex: 1 1 auto;
        }

        > .preorder-time-view-bottom {
            flex: 0 0 auto;
        }
    }

    .preorder-time-view-bottom {
        flex: 0 0 auto;
        padding: 18px 12px 12px;
        position: relative;
        background-color: #fff;
        -webkit-box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
        -moz-box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
        box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
        z-index: 400;
        pointer-events: none;
        transition: padding 0.6s variables.$easeOutExpo;

        > .paybtn {
            z-index: 420;
        }
    }

    // kiosk: the bar is the gap above the continue button, the taller button itself and
    // the gap under it - a share of the one above, as in every other kiosk bottom bar.
    // the 12px sides stay unscaled, the other kiosk bars keep theirs too
    .kiosk-mode .preorder-time-view-bottom {
        padding-top: 18px * variables.$kioskBottomScale;
        padding-bottom: 18px * variables.$kioskBottomScale * variables.$kioskBottomBtnGapFactor;
    }
</style>
